import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useGame } from '../context/GameContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUser, FiMail, FiLock, FiArrowRight, FiArrowLeft, FiAlertCircle, FiGift } = FiIcons;

const Login = ({ isSignUp = false }) => {
  const { signIn, signUp } = useGame();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [formData, setFormData] = useState({ name: '', email: '', password: '' });
  const [referralCode, setReferralCode] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Capture Referral Code from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get('ref');
    if (ref) {
      setReferralCode(ref);
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.email || !formData.password || (isSignUp && !formData.name)) {
      setError("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      if (isSignUp) {
        // Pass referralCode to signUp
        await signUp(formData.email, formData.password, formData.name, referralCode);
        // Auto sign in or show success message? Supabase usually auto signs in unless email confirm is on.
        // Assuming auto sign in for this demo context.
        navigate('/');
      } else {
        await signIn(formData.email, formData.password);
        navigate('/');
      }
    } catch (err) {
      console.error("Auth error:", err);
      setError(err.message || "Authentication failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background blobs */}
      <div className="absolute -top-40 -right-40 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl"></div>
      <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl"></div>

      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="w-full max-w-md bg-gray-800/80 backdrop-blur-md p-8 rounded-3xl border border-white/10 shadow-2xl relative z-10"
      >
        <Link to="/" className="absolute top-6 left-6 text-gray-400 hover:text-white transition-colors">
          <SafeIcon icon={FiArrowLeft} className="w-6 h-6" />
        </Link>

        <div className="text-center mb-8 mt-4">
          <h1 className="text-3xl font-bold text-white mb-2">{isSignUp ? 'Create Account' : 'Welcome Back'}</h1>
          <p className="text-gray-400">
            {isSignUp ? 'Start your earning journey today' : 'Enter your details to continue'}
          </p>
        </div>

        {error && (
          <div className="mb-4 bg-red-500/20 border border-red-500/50 rounded-xl p-3 flex items-start gap-3">
            <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-red-500 mt-0.5" />
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Referral Badge */}
        {isSignUp && referralCode && (
          <div className="mb-4 bg-green-500/20 border border-green-500/50 rounded-xl p-3 flex items-center gap-3">
            <SafeIcon icon={FiGift} className="w-5 h-5 text-green-400" />
            <div className="text-sm">
              <span className="text-green-300">Referred by code: </span>
              <span className="text-white font-bold tracking-wider">{referralCode}</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {isSignUp && (
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1 ml-1">Username</label>
              <div className="relative">
                <SafeIcon icon={FiUser} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  className="w-full bg-gray-900/50 border border-gray-600 rounded-xl py-3 pl-10 pr-4 text-white focus:outline-none focus:border-green-500 transition-colors"
                  placeholder="Enter your username"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1 ml-1">Email</label>
            <div className="relative">
              <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="email"
                value={formData.email}
                onChange={e => setFormData({ ...formData, email: e.target.value })}
                className="w-full bg-gray-900/50 border border-gray-600 rounded-xl py-3 pl-10 pr-4 text-white focus:outline-none focus:border-green-500 transition-colors"
                placeholder="hello@example.com"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1 ml-1">Password</label>
            <div className="relative">
              <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="password"
                value={formData.password}
                onChange={e => setFormData({ ...formData, password: e.target.value })}
                className="w-full bg-gray-900/50 border border-gray-600 rounded-xl py-3 pl-10 pr-4 text-white focus:outline-none focus:border-green-500 transition-colors"
                placeholder="••••••••"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold py-4 rounded-xl shadow-lg hover:shadow-green-500/25 transition-all flex items-center justify-center gap-2 mt-6 ${loading ? 'opacity-70 cursor-not-allowed' : 'hover:scale-[1.02]'}`}
          >
            {loading ? 'Processing...' : (isSignUp ? 'Sign Up' : 'Log In')}
            {!loading && <SafeIcon icon={FiArrowRight} />}
          </button>
        </form>

        <div className="mt-8 text-center text-sm text-gray-400">
          {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
          <Link to={isSignUp ? '/login' : `/signup${referralCode ? '?ref=' + referralCode : ''}`} className="text-green-400 font-bold hover:underline">
            {isSignUp ? 'Log In' : 'Sign Up'}
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;